/**
 * /outreach/sequences — "Sequence Execution".
 *
 * Real page replacing the ComingSoonPage fallthrough for the `seq-info` nav
 * item. Built from the v37 welcome prototype's `#page-seq-info` block
 * (D&D/meetings/2026-05-19/artifacts/nu_assist_v2_welcome_v37.html, line
 * ~1981) — the page that surfaces how sequence execution is split between
 * the web app and the NUCCE Chrome Extension.
 *
 * Page shape:
 *   1. Breadcrumb (Outreach / Sequences) + heading + subhead
 *   2. "How It Works" card with the dual-row flow diagram:
 *      - Row 1 — Thrive Contacts → NUCCE Extension → HubSpot Sequence →
 *        Reply Detected (teal solid: the auto-unenroll happy path).
 *      - Row 2 — LinkedIn Response → (shared NUCCE Extension) → HubSpot
 *        (amber dashed: the manual-unenroll path when the reply lands on
 *        LinkedIn instead of inside HubSpot).
 *      Both rows share the SAME NUCCE Extension node — that's the v18
 *      teaching point we surface in the note line below the diagram.
 *   3. Three CTAs:
 *      - Open Thrive (secondary, external)
 *      - Open HubSpot (secondary, external)
 *      - Install NUCCE* (primary, per-stage CWS listing URL; the
 *        asterisk anchors the inline footnote about needing to be
 *        signed into the NU Google account before installing)
 *
 * Per-stage Install CTA: `getCwsListingUrl(resolveStage())` from
 * `@nu-assist/shared-fe` returns the CWS listing URL for `dev`/`prod`
 * and `null` for `local` (never published). When `null`, we render the
 * button as disabled with explanatory copy rather than as a broken
 * external link.
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns
 * ONLY its page content — same pattern as `AgendaForm` and the bookmarklet
 * tool pages.
 *
 * Tokenized: no inline `style={{}}`. Color/spacing/radius all come from
 * `@nu-assist/ui` tokens (`text-text`, `border-border`, `bg-accent-muted`,
 * `text-warning-ink`, `border-warning-strong`, `rounded-md`, etc.).
 */
import { ExternalLink } from "lucide-react";
import {
  formatRelativeTime,
  getCwsListingUrl,
  useLatestExtensionVersion,
} from "@nu-assist/shared-fe";
import { Button } from "@nu-assist/ui";
import { resolveStage } from "../../../config/featureFlags";

// ---------------------------------------------------------------------------
// External app links — Thrive + HubSpot
// ---------------------------------------------------------------------------

/**
 * Thrive TRM web app. Matches the URL hard-coded in the v37 prototype's
 * "Open Thrive" CTA. This is the recruiters' day-to-day Thrive entry
 * point — not a per-stage URL.
 */
const THRIVE_URL = "https://thrive.nusearch.com";

/** HubSpot app home — same as the prototype's "Open HubSpot" CTA. */
const HUBSPOT_URL = "https://app.hubspot.com";

// ---------------------------------------------------------------------------
// Flow diagram primitive
// ---------------------------------------------------------------------------

/**
 * A single box in the flow diagram (Thrive Contacts, HubSpot Sequence,
 * etc.). Kept as a local primitive — it's only used inside this page and
 * carrying it into `@nu-assist/ui` would over-generalize.
 *
 * `tone="manual"` switches the box to the amber-dashed treatment used by
 * the row-2 manual-unenroll path — the prototype's `border-style: dashed`
 * + amber border + amber heading text.
 */
function FlowBox({
  title,
  body,
  tone = "default",
}: {
  title: string;
  body: string;
  tone?: "default" | "manual";
}) {
  const base =
    "flex flex-col items-center justify-center rounded-md border bg-surface px-3 py-3 text-center";
  const toneClasses =
    tone === "manual"
      ? "border-dashed border-warning-strong"
      : "border-border";
  const titleColor =
    tone === "manual" ? "text-warning-ink" : "text-text";
  return (
    <div className={`${base} ${toneClasses}`}>
      <h5 className={`text-label font-semibold ${titleColor}`}>{title}</h5>
      <p className="text-caption text-text-secondary">{body}</p>
    </div>
  );
}

/**
 * Inline arrow connector. Color is driven by tone so the auto path stays
 * teal (`text-accent`) and the manual path goes amber
 * (`text-warning-ink`).
 */
function FlowArrow({ tone = "default" }: { tone?: "default" | "manual" }) {
  const color = tone === "manual" ? "text-warning-ink" : "text-accent";
  return (
    <span aria-hidden="true" className={`text-h3 font-bold ${color}`}>
      &rarr;
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SequencesPage() {
  const stage = resolveStage();
  const cwsUrl = getCwsListingUrl(stage);
  const latest = useLatestExtensionVersion();
  // The version users can install is exposed by the CWS API only via the
  // DRAFT projection (-> `coming_soon`) for a private (trusted testers)
  // listing: `available` (PUBLISHED projection) is null forever in that
  // mode. Prefer `available` when present, else fall back to `coming_soon`.
  const availableVersion =
    latest.data?.current.available?.manifest_version ??
    latest.data?.current.coming_soon?.manifest_version ??
    null;
  // Only `available.live_since` is a true "went live" timestamp. We do NOT
  // fall back to `coming_soon.submitted_at` here — that's a *submission*
  // time, and rendering it next to "Latest vX" would mislabel it as a
  // go-live time. When only `coming_soon` exists we show the version with
  // no relative-time suffix.
  const availableSince = latest.data?.current.available?.live_since ?? null;

  return (
    <div data-testid="sequences-page" className="p-12">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <nav
          aria-label="Breadcrumb"
          className="mb-2 text-caption text-text-secondary"
        >
          Outreach <span className="px-1">/</span>
          <span className="text-accent">Sequences</span>
        </nav>
        <h1 className="mb-2 text-h1 text-text">Sequence Execution</h1>
        <p className="mb-6 text-body text-text-secondary">
          You can add Contacts to HubSpot Sequences in a variety of ways, including using the NU Custom Chrome Extension NUCCE.
        </p>


        {/* How It Works card */}
        <section className="mb-6 rounded-md border border-border bg-surface p-6">
          <h2 className="mb-3 text-h3 text-text">How It Works</h2>
          <p className="mb-5 text-body text-text-secondary">
            From a Search page in Thrive, select contacts, then use NUCCE to validate their data and enroll them in a HubSpot
            sequence. The extension handles enrollment, change their status to Pursuing, monitors for replies, and automatically unenrolls Contacts who respond. You still have to unenroll a Contact manually if they respond through another method such as LinkedIn or text.
          </p>

          {/*
            Flow diagram. The prototype uses a 5-column CSS grid where the
            NUCCE Extension cell (column 3) spans both rows — the visual
            point is that BOTH the auto-unenroll path and the manual
            LinkedIn-reply path funnel through the same extension node.
            We mirror that with a Tailwind grid (5 cols, 2 rows) — the
            NUCCE cell uses `row-span-2` to bridge the two flow rows.
          */}
          {/*
            5-column grid via `grid-cols-[…]` token (Tailwind JIT). Inline
            `style={{}}` is forbidden by the workspace's hard-rule gate, so
            the column track sizes live in the className. `minmax(0,…fr)`
            lets the box cells shrink without overflowing the viewport.
          */}
          <div
            className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.2fr)_auto_minmax(0,1.6fr)] gap-3 md:gap-4"
            data-testid="sequences-flow"
          >
            {/* Row 1, col 1 — Thrive Contacts (auto-path source) */}
            <div className="col-start-1 row-start-1">
              <FlowBox title="Thrive Contacts" body="Select candidates" />
            </div>
            {/* Row 1, col 2 — auto arrow */}
            <div className="col-start-2 row-start-1 flex items-center justify-center">
              <FlowArrow />
            </div>
            {/*
              Col 3, rows 1+2 — SHARED NUCCE Extension box. Highlighted
              with the accent (teal) background to mark it as the central
              node both flows pass through.
            */}
            <div className="col-start-3 row-span-2 row-start-1 flex flex-col items-center justify-center rounded-md border border-accent bg-accent-muted px-4 py-4 text-center">
              <h5 className="mb-1 text-label font-bold text-accent">
                NUCCE Auto Adds to HubSpot
              </h5>
              <p className="text-caption text-text-secondary">
              </p>
              <div
                aria-hidden="true"
                className="my-2 h-px w-3/4 bg-warning-strong"
              />
              <p className="text-caption text-warning-ink">
                Recruiter clicks Unenroll{" "} in NUCCE to manually unenroll from sequence if Contact responded outside of email
              </p>
            </div>
            {/* Row 1, col 4 — auto arrow */}
            <div className="col-start-4 row-start-1 flex items-center justify-center">
              <FlowArrow />
            </div>
            {/*
              Row 1, col 5 — HubSpot Sequence + Reply Detected pair.
              The prototype renders these as a 2-box mini-flow inside the
              5th cell, joined by a sub-arrow.
            */}
            <div className="col-start-5 row-start-1 flex items-stretch gap-2">
              <div className="flex-1">
                <FlowBox
                  title="HubSpot Sequence"
                  body="Automated outreach"
                />
              </div>
              <div className="flex items-center justify-center">
                <FlowArrow />
              </div>
              <div className="flex-1">
                <FlowBox title="Reply Detected" body="Auto-unenroll" />
              </div>
            </div>

            {/* Row 2, col 1 — LinkedIn Response (manual-path source) */}
            <div className="col-start-1 row-start-2">
              <FlowBox
                tone="manual"
                title="LinkedIn Response"
                body="Reply spotted on LinkedIn"
              />
            </div>
            {/* Row 2, col 2 — manual arrow */}
            <div className="col-start-2 row-start-2 flex items-center justify-center">
              <FlowArrow tone="manual" />
            </div>
            {/* Row 2, col 4 — manual arrow */}
            <div className="col-start-4 row-start-2 flex items-center justify-center">
              <FlowArrow tone="manual" />
            </div>
            {/* Row 2, col 5 — HubSpot (manual unenroll API) */}
            <div className="col-start-5 row-start-2">
              <FlowBox
                tone="manual"
                title="HubSpot"
                body="Manual unenroll API"
              />
            </div>
          </div>
        </section>

        {/* CTAs */}
        <div className="flex flex-wrap items-start gap-3">
          <a
            href={THRIVE_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="sequences-open-thrive"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3.5 py-2 text-label font-semibold text-text-secondary transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <ExternalLink size={14} aria-hidden="true" />
            Open Thrive
          </a>
          <a
            href={HUBSPOT_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="sequences-open-hubspot"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3.5 py-2 text-label font-semibold text-text-secondary transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <ExternalLink size={14} aria-hidden="true" />
            Open HubSpot
          </a>
          {/*
            Install NUCCE* — primary CTA. Per-stage CWS listing URL via
            getCwsListingUrl(); null on `local` (Load-Unpacked only).
            When null we render a disabled <Button> with copy explaining
            why, instead of a broken link. The asterisk anchors the
            inline footnote below ("you MUST be in your NU Google
            account") — keep the label literal in sync if the footnote
            is ever moved or removed.
          */}
          <div className="inline-flex flex-col gap-1.5">
            <div className="inline-flex items-center gap-3">
              {cwsUrl ? (
                <a
                  href={cwsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="sequences-install-nucce"
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent px-3.5 py-2 text-label font-semibold text-bg transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                >
                  Install NUCCE*
                </a>
              ) : (
                <Button
                  type="button"
                  disabled
                  data-testid="sequences-install-nucce-disabled"
                  title="The local stage uses Load-Unpacked; there is no Chrome Web Store listing."
                >
                  Install NUCCE*
                </Button>
              )}
              {availableVersion ? (
                <span
                  data-testid="sequences-install-nucce-version"
                  className="text-caption text-text-secondary"
                >
                  Latest <span className="font-semibold text-text">v{availableVersion}</span>
                  {availableSince ? ` · ${formatRelativeTime(availableSince)}` : ""}
                </span>
              ) : null}
            </div>
            <p className="text-caption text-text-secondary">
              * To install NUCCE, you MUST be in your NU Google account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
